/**
 * Utility functions for formatting tooth tooltip content
 */

import { omitAtomicsCoveredByCompounds } from '@utils/charting-surface-display.utils'

const surfaceMap: Record<string, string> = {
  m: 'M',
  d: 'D',
  b: 'B',
  o: 'O',
  p: 'P',
  i: 'I',
  mb: 'MOB',
  db: 'DOB',
  mp: 'MOP',
  dp: 'DOP',
  cb: 'CB',
  cp: 'CP',
  bc: 'BC',
  bs: 'BS',
  pc: 'PC',
  ps: 'PS',
  l: 'L',
  ls: 'LS',
  lc: 'LC',
  cl: 'CL',
  dlc: 'DOL',
  mlc: 'MOL',
  mifp: 'MIFP',
  difp: 'DIFP',
  mifl: 'MIFL',
  difl: 'DIFL',
}

// Order of surfaces based on surfaceMap
const surfaceOrder = Object.keys(surfaceMap)

export const formatSurface = (surface: string): string => {
  return surfaceMap[surface] || surface
}

export const formatSurfaces = (surfaces: string[]): string => {
  if (surfaces.length === 0) return ''
  const displaySurfaces = omitAtomicsCoveredByCompounds(surfaces)
  if (displaySurfaces.length === 0) return ''
  // Sort surfaces based on the order in surfaceMap
  const sortedSurfaces = [...displaySurfaces].sort((a, b) => {
    const indexA = surfaceOrder.indexOf(a)
    const indexB = surfaceOrder.indexOf(b)
    // If surface not found in map, put it at the end
    if (indexA === -1 && indexB === -1) return 0
    if (indexA === -1) return 1
    if (indexB === -1) return -1
    return indexA - indexB
  })
  return `(${sortedSurfaces.map(formatSurface).join(', ')})`
}

export const formatMaterial = (material: string | undefined): string => {
  if (!material) return ''
  const materialMap: Record<string, string> = {
    gold: 'Gold',
    zirconia: 'Zirconia',
    ceramic: 'Ceramic',
    composite: 'Composite',
    amalgam: 'Amalgam',
    pfm: 'PFM',
    pfz: 'PFZ',
    metal: 'Metal',
  }
  return materialMap[material] || material
}

export const formatType = (type: string | undefined): string => {
  if (!type) return ''
  return type.toUpperCase()
}

export const formatSeverity = (severity: string | undefined): string => {
  if (!severity) return ''
  return severity.charAt(0).toUpperCase() + severity.slice(1)
}

export const formatDirection = (direction: string | undefined): string => {
  if (!direction) return ''
  return direction.charAt(0).toUpperCase() + direction.slice(1)
}

export const formatFractureType = (type: string | undefined): string => {
  if (!type) return ''
  if (type === 'vertical_root') return 'Root fracture (vertical)'
  if (type === 'horizontal_root') return 'Root fracture (horizontal)'
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const formatCrackType = (type: string | undefined): string => {
  return formatFractureType(type)
}

export const formatRecessionClass = (type: string | undefined): string => {
  if (!type) return ''
  return `Class ${type.replace('class', '')}`
}

export const formatFurcationLevel = (level: string | undefined): string => {
  if (!level) return ''
  const upperLevel = level.toUpperCase()
  if (/^C[1-3]$/.test(upperLevel)) {
    return upperLevel
  }
  return level
}

export const formatBridgeSupport = (
  support:
    | 'left_bridge'
    | 'left_abutment'
    | 'right_bridge'
    | 'right_abutment'
    | 'right_left_bridge'
    | 'right_left_abutment'
    | undefined,
): string => {
  if (!support) return ''
  if (support === 'left_bridge') return 'Left Bridge'
  if (support === 'left_abutment') return 'Left Abutment'
  if (support === 'right_bridge') return 'Right Bridge'
  if (support === 'right_abutment') return 'Right Abutment'
  if (support === 'right_left_bridge') return 'Right-Left Bridge'
  if (support === 'right_left_abutment') return 'Right-Left Abutment'
  return support
}

const surfaceFullNameMap: Record<string, string> = {
  m: 'Mesial',
  d: 'Distal',
  b: 'Facial',
  o: 'Occlusal',
  p: 'Palatal',
  i: 'Incisal',
  mb: 'Mesial Occlusal Buccal',
  mob: 'Mesial Occlusal Buccal',
  db: 'Distal Occlusal Buccal',
  dob: 'Distal Occlusal Buccal',
  mp: 'Mesial Occlusal Palatal',
  mop: 'Mesial Occlusal Palatal',
  dp: 'Distal Occlusal Palatal',
  dop: 'Distal Occlusal Palatal',
  mlc: 'Mesial Occlusal Lingual',
  mol: 'Mesial Occlusal Lingual',
  dlc: 'Distal Occlusal Lingual',
  dol: 'Distal Occlusal Lingual',
  cb: 'Cervicofacial',
  cp: 'Cervicopalatal',
  bc: 'Buccal Cusp',
  bs: 'Faciosulcal',
  pc: 'Palatal Cusp',
  ps: 'Palatosulcal',
  l: 'Lingual',
  ls: 'Lingual Sulcal',
  lc: 'Lingual Cusp',
  cl: 'Cervicolingual',
  mifp: 'Mesial Incisal Facial Palatal',
  difp: 'Distal Incisal Facial Palatal',
  mifl: 'Mesial Incisal Facial Lingual',
  difl: 'Distal Incisal Facial Lingual',
}

export const formatSurfaceFullName = (surface: string): string => {
  return surfaceFullNameMap[surface] || formatSurface(surface)
}

export const formatSurfacesFullNames = (surfaces: string[]): string => {
  if (surfaces.length === 0) return ''
  const displaySurfaces = omitAtomicsCoveredByCompounds(surfaces)
  if (displaySurfaces.length === 0) return ''
  // Sort surfaces based on the order in surfaceMap
  const sortedSurfaces = [...displaySurfaces].sort((a, b) => {
    const indexA = surfaceOrder.indexOf(a)
    const indexB = surfaceOrder.indexOf(b)
    // If surface not found in map, put it at the end
    if (indexA === -1 && indexB === -1) return 0
    if (indexA === -1) return 1
    if (indexB === -1) return -1
    return indexA - indexB
  })
  return sortedSurfaces.map(formatSurfaceFullName).join(', ')
}
